import { Timestamp } from 'firebase/firestore';

export type AccountType = 'college' | 'alumni';
export type Plan = 'starter' | 'growth' | 'campus' | 'university';

export const PLAN_LIMITS: Record<Plan, number> = {
  starter: 500,
  growth: 1000,
  campus: 2000,
  university: 5000,
};

export const PLAN_PRICING: Record<AccountType, Record<Plan, string>> = {
  college: {
    starter: '$19,999',
    growth: '$34,999',
    campus: '$54,999',
    university: '$99,999',
  },
  alumni: {
    starter: '$24,999',
    growth: '$44,999',
    campus: '$69,999',
    university: '$124,999',
  },
};

export interface CollegeAccount {
  id?: string;
  name: string;
  type: AccountType;
  city: string;
  state: string;
  website: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  domainEnabled: boolean;
  domain?: string;
  csvEnabled: boolean;
  plan: Plan;
  studentLimit: number;
  startDate: string;
  expiryDate: string;
  active: boolean;
  notes: string;
  adminEmails?: string[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface CollegeStudent {
  email: string;
  name: string;
  accountId: string;
  accountName?: string;
  activated: boolean;
  activatedAt?: Timestamp;
  addedAt?: Timestamp;
  graduationYear?: string | null;
}

// Normalize a raw Firestore document into a CollegeAccount, migrating the
// legacy single-field activationMethod to the new dual-boolean schema.
export function normalizeAccount(id: string, data: Record<string, any>): CollegeAccount {
  const legacy: string | undefined = data.activationMethod;
  return {
    ...data,
    id,
    domainEnabled: data.domainEnabled ?? legacy === 'domain',
    csvEnabled: data.csvEnabled ?? legacy === 'csv',
  } as CollegeAccount;
}

export interface FirmAccount {
  id?: string;
  name: string;
  contactEmail: string;
  adminEmails?: string[];
  seatCount: number;
  seatsUsed: number;
  active: boolean;
  termStartDate?: Timestamp;
  termEndDate?: Timestamp;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface CoachRecord {
  id?: string;
  firmId: string;
  name: string;
  email: string;
  uid?: string;
  status: 'active' | 'inactive';
  coachType?: 'independent' | 'firm' | 'college';
  firmName?: string | null;
  createdAt?: Timestamp;
}

export function normalizeFirm(id: string, data: Record<string, any>): FirmAccount {
  return { ...data, id } as FirmAccount;
}

export function normalizeCoach(id: string, data: Record<string, any>): CoachRecord {
  return { ...data, id } as CoachRecord;
}
